import { postJson } from "./http";

export interface TallyDutyLedger {
  name: string;
  guid: string | null;
}

export interface TallyTaxSide {
  sample_name: string;
  status: "pending" | "mapped" | "failed" | null;
  /** creating: import in flight. confirming: reading the chart back. */
  phase?: "creating" | "confirming" | null;
  tally_ledger_name: string | null;
  error: string | null;
}

export interface TallyTaxMapRow {
  id: number;
  kind: "component" | "tds" | "tcs";
  tax_type: string;
  percentage: string;
  section: string;
  sample_name: string;
  /** Catalogue display name. Applicability text, copied onto the tenant at go-live. */
  tax_name?: string;
  applicability: string;
  expired: boolean;
  origin: "govt" | "firm" | string;
  threshold: string | null;
  status: "pending" | "mapped" | "failed" | null;
  /** creating: import in flight. confirming: reading the chart back. */
  phase?: "creating" | "confirming" | null;
  tally_ledger_name: string | null;
  error: string | null;
  /** Input ledger for bills. Present on GST components. */
  bill?: TallyTaxSide;
  /** Output ledger for invoices. Present on GST components. */
  invoice?: TallyTaxSide;
}

export interface TallyRegistration {
  id: number | null;
  name: string;
  gstin: string;
  state: string;
  registration_type: string;
  is_primary: boolean;
  origin: "tally" | "manual";
}

export interface TallyTaxState {
  gstin: string;
  gst_state: string;
  gst_registration_type: string;
  registrations?: TallyRegistration[];
  /** True while a create or alter is in flight, or the tax-units re-read after it has not finished. */
  registrations_writing?: boolean;
  /** Tally refused the write, or the re-read showed it kept the previous GSTIN. */
  registration_error?: string | null;
  duty_ledgers: TallyDutyLedger[];
  components: TallyTaxMapRow[];
  tds: TallyTaxMapRow[];
  tcs: TallyTaxMapRow[];
  components_mapped: boolean;
  rates_mapped: boolean;
  /** True while a company re-read that includes GST details is still in flight. */
  gst_refreshing?: boolean;
}

interface DataEnvelope<T> {
  data: T;
}

const state = (publicId: string) =>
  postJson<DataEnvelope<TallyTaxState>>("/api/onboarding/tally/tax/state", { public_id: publicId }).then((r) => r.data);

const profile = (publicId: string, fields: { gstin: string; gst_state: string; gst_registration_type: string }) =>
  postJson<DataEnvelope<TallyTaxState>>("/api/onboarding/tally/tax/profile", { public_id: publicId, ...fields }).then((r) => r.data);

const saveRegistrations = (publicId: string, registrations: TallyRegistration[]) =>
  postJson<DataEnvelope<TallyTaxState>>("/api/onboarding/tally/tax/registrations", {
    public_id: publicId,
    registrations,
  }).then((r) => r.data);

const mapLedger = (publicId: string, kind: string, catalogueId: number, tallyLedgerName: string, side?: "invoice" | "bill") =>
  postJson<DataEnvelope<TallyTaxState>>("/api/onboarding/tally/tax/map", {
    public_id: publicId,
    kind,
    catalogue_id: catalogueId,
    tally_ledger_name: tallyLedgerName,
    side,
  }).then((r) => r.data);

const createLedger = (publicId: string, kind: string, catalogueId: number, tallyLedgerName: string, side?: "invoice" | "bill") =>
  postJson<DataEnvelope<TallyTaxState>>("/api/onboarding/tally/tax/create", {
    public_id: publicId,
    kind,
    catalogue_id: catalogueId,
    tally_ledger_name: tallyLedgerName,
    side,
  }).then((r) => r.data);

const createMissing = (publicId: string, kind: "component" | "tds" | "tcs") =>
  postJson<DataEnvelope<TallyTaxState>>("/api/onboarding/tally/tax/create-missing", { public_id: publicId, kind }).then(
    (r) => r.data
  );

const addRate = (
  publicId: string,
  fields: {
    kind: "tds" | "tcs";
    section: string;
    percentage: string;
    applicability: string;
    threshold: string;
    expired: boolean;
  }
) =>
  postJson<DataEnvelope<TallyTaxState>>("/api/onboarding/tally/tax/rate", { public_id: publicId, ...fields }).then((r) => r.data);

export const tallyTaxApi = { state, profile, saveRegistrations, mapLedger, createLedger, createMissing, addRate };

export function taxMapBusy(rows: TallyTaxMapRow[]): boolean {
  return rows.some((row) => {
    if (row.bill || row.invoice) {
      return row.bill?.status === "pending" || row.invoice?.status === "pending";
    }

    return row.status === "pending";
  });
}
